import type { ChallengeSelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { BadgeList } from '@components/shared/badge-list.component';
import { ExpandableText } from '@components/shared/expandable-text.component';
import { ArrowRight, Layers } from 'lucide-react';
import type { FC } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type ChallengeCardProps = {
  challenge: ChallengeSelectDto;
};

export const ChallengeCard: FC<ChallengeCardProps> = (props) => {
  const { challenge } = props;
  const navigate = useNavigate();
  const [isHovering, setIsHovering] = useState(false);

  const handleView = () => {
    navigate(`/tech-challenges/${challenge.id}`);
  };

  const phaseCount = challenge.phases?.length ?? 0;

  const phaseBadges =
    challenge.phases?.map((phase) => ({
      id: phase.id,
      name: phase.name,
    })) ?? [];

  return (
    <div
      className="bg-white rounded-md flex flex-col gap-2 p-3 border border-gray-200"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="flex justify-between gap-2 items-center">
        <div className="flex-1">
          <h3
            className="text-base font-medium text-gray-600 cursor-pointer hover:underline"
            onClick={handleView}
          >
            {challenge.name}
          </h3>
        </div>
        <IconButton
          ariaLabel="View Challenge"
          icon={<ArrowRight />}
          isVisible={isHovering}
          onClick={handleView}
          variant="secondary"
        />
      </div>

      {challenge.description && (
        <div className="pr-10">
          <ExpandableText
            className="text-xs text-gray-600"
            maxLines={3}
            text={challenge.description}
          />
        </div>
      )}

      <div className="flex flex-col gap-3">
        {phaseCount > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 whitespace-nowrap">
              <Layers size={14} />
              <span>
                {phaseCount} {phaseCount === 1 ? 'Phase' : 'Phases'}
              </span>
            </div>
            <BadgeList
              badgeVariant="secondary"
              items={phaseBadges}
              limit={5}
              size="xs"
            />
          </div>
        )}
      </div>
    </div>
  );
};
